import { type SynopticCategory } from '@core/referential/synoptic-category.enum'
import { SynopticType } from '@core/referential/synoptic-type.enum'
import { GetSynopticsResponseSchema } from '@shared/schema/synoptic.get-all.schema'
import { queryOptions } from '@tanstack/react-query'

import { type QueryOptionsData } from '@/@types/query'
import { apiFetch } from '@/lib/api-fetch'

// Préfixe commun à toutes les catégories et à tous les sites : les mutations invalident dessus pour
// rafraîchir les listes sans avoir à connaître la catégorie du synoptique modifié.
export const SITE_MODULE_SYNOPTICS_QUERY_KEY = ['module-synoptic', 'synoptics'] as const

// Synoptiques générés d'un site pour une catégorie à modules (chaufferie, PAC / groupe froid), avec
// les noms de site / bâtiment / étage résolus à plat : la vue technique s'en sert pour retrouver le
// synoptique courant, le localiser et paginer vers ses voisins. La réponse est cloisonnée côté API
// (société + user_site) par le paramètre `siteId`.
export function siteModuleSynopticsQuery(category: SynopticCategory, siteId: number) {
  return queryOptions({
    queryKey: [...SITE_MODULE_SYNOPTICS_QUERY_KEY, category, siteId] as const,
    async queryFn() {
      const searchParams = new URLSearchParams({ siteId: String(siteId), type: SynopticType.SCHEMA, category })

      const rawResponse = await apiFetch.get('synoptic', { searchParams })

      if (!rawResponse.ok) {
        throw new Error(`Failed to fetch ${category} synoptics`)
      }

      try {
        const synoptics = await GetSynopticsResponseSchema.parseAsync(await rawResponse.json())

        // La requête est scopée par site : l'API n'a pu renvoyer que des synoptics rattachés à
        // celui-ci. On resserre le type que l'endpoint garde volontairement large (un template n'a
        // pas de site) plutôt que d'imposer un `?? 0` à chaque lecture côté vue.
        return synoptics.map((synoptic) => ({ ...synoptic, siteId: synoptic.siteId ?? siteId, siteName: synoptic.siteName ?? '' }))
      } catch (error) {
        throw new Error(`Failed to parse ${category} synoptics response`, { cause: error })
      }
    },
  })
}

export type SiteModuleSynoptic = QueryOptionsData<ReturnType<typeof siteModuleSynopticsQuery>>[number]
